import { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Subject } from "rxjs";
import {
  setCheckedGlobalTypeWells,
  setCheckedKeys,
  setExpandedKeys,
  setFilterId,
  setGlobalGroupBy
} from "store/features";
// eslint-disable-next-line no-restricted-imports -- intentional dual-write for cross-tab redux-state-sync; see EVA-6684
import { setSelectedWells as setSelectedWellsRedux } from "store/features/map/mapSlice";
import {
  setScreenshotPresets,
  setTypeWellSettings
} from "store/features/userSettings/userSettingsSlice";
import { RootState } from "store/rootReducer";
import { useMapStore, useTxnIdCrossTabSubscriber } from "stores/map";
import { useFilterPendingCrossTabSubscriber } from "stores/useFilterPendingCrossTabSync";
import { useWorkspaceStore } from "stores/workspace/workspaceStore";

import { useUser } from "hooks";

import { getScreenshotPreset, getUserOrgScreenshots } from "api/users";

import { EntityKind } from "models/entityKind";

import { ErrorBoundary } from "components/base";
import NewWindowDashboard from "components/dashboard/NewWindowDashboard";
import { DashboardProvider } from "components/dashboard/contexts/DashboardContext";
import { DashboardContextState } from "components/dashboard/contexts/DashboardContextState";
import { useGetCategoryList } from "components/groupBy/hooks/useGetCategoryList";
import { IconSpinner } from "components/icons";
import { ScreenshotProvider } from "components/screenshot/contexts/ScreenshotContext";
import { ScreenshotPresetProvider } from "components/screenshot/contexts/ScreenshotPresetContext";
import { SessionInfo } from "components/user";
import { useUserContext } from "components/user/context";
import { updateSession } from "components/user/context/reducer/UserReducer";
import { WorkspaceProvider } from "components/workspace/contexts/WorkspaceContext";

import useTypeWellSettings from "../api/useTypeWellSettings";
import { ArpsProvider } from "../components/arps/context";
import ProjectWrapper from "../components/project/projects/components/ProjectWrapper";
import { ProjectProvider } from "../components/project/projects/context";
import { WellListProvider } from "../components/well-list/context";
import { TYPE_WELLS } from "../constants";
import { useReduxSelectedWellsRelay } from "./useReduxSelectedWellsRelay";

function StandaloneDashboard(): JSX.Element {
  const dispatch = useDispatch();
  const [, userDispatch] = useUserContext();

  const channelId = useSelector((state: RootState) => state.channel.channel);

  const presets = useSelector((state: RootState) => state.userSetting.screenshotPreset);

  const token = useSelector((state: RootState) => state.auth.jwtToken);

  const categories = useSelector((state: RootState) => state.groupBy.categoryList);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isTokenSet, setIsTokenSet] = useState(false);
  const [dashboardState, setDashboardState] = useState<DashboardContextState>();

  const setWorkspaceId = useWorkspaceStore((s) => s.setWorkspaceId);
  const setActiveDashboardId = useWorkspaceStore((s) => s.setActiveDashboardId);
  const setTxnId = useMapStore((s) => s.setTxnId);

  // Relay Redux well selections into Zustand so child-tab stays live (EVA-6684)
  useReduxSelectedWellsRelay();

  // Keep the map txnId (legend items, counts, colours) live after the opening
  // snapshot below, same as PopoutChartPane (EVA-124).
  useTxnIdCrossTabSubscriber();
  useFilterPendingCrossTabSubscriber();

  const { user } = useUser();

  const channelData = sessionStorage.getItem(channelId);
  const initialState = channelData ? JSON.parse(channelData) : null;

  const { data, refetch } = useTypeWellSettings();

  useEffect(() => {
    if (!data || !data.settings?.length) {
      refetch();
      return;
    }

    dispatch(setTypeWellSettings(data));
  }, [dispatch, data]);

  useEffect(() => {
    if (!initialState) return;

    if (!isInitialized) {
      if (initialState.filterId) {
        dispatch(setFilterId(initialState.filterId));
      }

      if (initialState.settings) {
        setWorkspaceId(initialState.settings.workspaceId);
        setActiveDashboardId(initialState.settings.dashboardId);
        setDashboardState({
          lockMap: true,
          isLoading: false,
          isModified: false,
          hasModifiedWidgets: false,
          dashboardRefreshObserver: new Subject(),
          dashboardMode: "explore"
        });
      }

      if (initialState.groupBy) {
        dispatch(setGlobalGroupBy(initialState.groupBy));
      }

      if (initialState.txnId) {
        setTxnId(initialState.txnId);
      }

      if (initialState?.selectedWells) {
        // Dispatch to Redux so redux-state-sync and the relay below keep the
        // child tab live on subsequent parent-tab well selections (EVA-6684).
        dispatch(setSelectedWellsRedux(initialState.selectedWells));
      }

      if (initialState?.typeWells?.checkedGlobalTypeWells) {
        dispatch(
          setCheckedGlobalTypeWells(initialState.typeWells.checkedGlobalTypeWells)
        );
      }

      if (initialState?.typeWells?.checkedTypeWellKeys) {
        dispatch(
          setCheckedKeys({
            type: TYPE_WELLS,
            checkedKeys: initialState.typeWells.checkedTypeWellKeys
          })
        );
      }

      if (initialState?.typeWells?.expandedTypeWellKeys) {
        dispatch(
          setExpandedKeys({
            type: TYPE_WELLS,
            expandedKeys: initialState.typeWells.expandedTypeWellKeys
          })
        );
      }

      if (initialState?.sessionId) {
        updateSession(userDispatch, initialState.sessionId);
      }

      setIsInitialized(true);
    }
  }, [dispatch, initialState, isInitialized]);

  // Component mounting effects
  useEffect(() => {
    if (!presets.length && user) {
      const userPresets = getScreenshotPreset();
      const orgPresets = getUserOrgScreenshots();
      Promise.all([orgPresets, userPresets])
        .then((values) => {
          dispatch(setScreenshotPresets([...values[0], ...values[1]]));
        })
        // eslint-disable-next-line no-console
        .catch(console.log);
    }
  }, [dispatch, presets.length, user]);

  useEffect(() => {
    if (!isTokenSet && token) {
      setIsTokenSet(true);
    }
  }, [dispatch, isTokenSet, token]);

  // Component mounting effects
  const { getCategoryListWithUnit, isReady } = useGetCategoryList();
  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (isTokenSet && !categories.length) {
      // only get categories/presets if token is set
      getCategoryListWithUnit(EntityKind.Well);
    }
  }, [dispatch, isTokenSet, isReady]);

  if (!token) {
    return null;
  }

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div
            style={{
              display: "grid",
              justifyContent: "center",
              alignItems: "center"
            }}>
            <div>
              Loading...
              <div>
                <IconSpinner></IconSpinner>
              </div>
            </div>
          </div>
        }>
        <WorkspaceProvider useWrapper={false}>
          <DashboardProvider state={dashboardState}>
            <ScreenshotProvider>
              <ScreenshotPresetProvider>
                <ProjectProvider>
                  <ProjectWrapper>
                    <WellListProvider>
                      <ArpsProvider>
                        <NewWindowDashboard />
                      </ArpsProvider>
                    </WellListProvider>
                  </ProjectWrapper>
                </ProjectProvider>
              </ScreenshotPresetProvider>
            </ScreenshotProvider>
          </DashboardProvider>
        </WorkspaceProvider>
      </Suspense>
      <SessionInfo />
    </ErrorBoundary>
  );
}

export default StandaloneDashboard;
